import { useState, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import * as openpgp from 'openpgp';
import ExcelJS from 'exceljs';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';

// ─── CSV parsing (handles quoted fields with commas inside) ────────────────────
function parseCSVLine(line) {
  const fields = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      fields.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  fields.push(cur.trim());
  return fields;
}

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, ''));
  return lines.slice(1).filter(l => l.trim()).map(line => {
    const fields = parseCSVLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (fields[i] || '').replace(/^"|"$/g, ''); });
    return obj;
  });
}

async function extractCSVFromZip(file) {
  const zip = await JSZip.loadAsync(file);
  for (const [name, entry] of Object.entries(zip.files)) {
    if (!entry.dir && name.toLowerCase().includes('account-download-details') && name.toLowerCase().endsWith('.csv')) {
      return await entry.async('text');
    }
  }
  for (const [name, entry] of Object.entries(zip.files)) {
    if (!entry.dir && name.toLowerCase().endsWith('.csv')) {
      return await entry.async('text');
    }
  }
  return null;
}

async function parseXLSX(file) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const ws = wb.worksheets[0];
  if (!ws) return [];
  const rows = [];
  let headers = [];
  ws.eachRow((row, rowNum) => {
    const vals = row.values.slice(1).map(v => (v == null ? '' : String(v).trim()));
    if (rowNum === 1) { headers = vals; }
    else {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
      if (Object.values(obj).some(v => v)) rows.push(obj);
    }
  });
  return rows;
}

// ─── GPG helpers ───────────────────────────────────────────────────────────────
async function sha256hex(bytes) {
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function decryptGPG(encryptedBytes, passphrase) {
  // Detect ASCII-armored format
  const headerText = new TextDecoder('utf-8', { fatal: false }).decode(encryptedBytes.slice(0, 27));
  if (headerText.startsWith('-----BEGIN PGP')) {
    const message = await openpgp.readMessage({ armoredMessage: new TextDecoder().decode(encryptedBytes) });
    const { data } = await openpgp.decrypt({ message, passwords: [passphrase], format: 'binary' });
    return data instanceof Uint8Array ? data : new Uint8Array(data);
  }

  // Validate first byte is a valid OpenPGP packet tag (must be >= 0x80)
  if (encryptedBytes[0] < 0x80) {
    const preview = new TextDecoder('utf-8', { fatal: false }).decode(encryptedBytes.slice(0, 100));
    throw new Error(`Not a GPG file — starts with: "${preview.slice(0, 60)}". The download may have failed or returned an error page.`);
  }

  const message = await openpgp.readMessage({ binaryMessage: encryptedBytes });
  const { data } = await openpgp.decrypt({ message, passwords: [passphrase], format: 'binary' });
  return data instanceof Uint8Array ? data : new Uint8Array(data);
}

async function resolvePassphrase(fileLink, userPassphrase) {
  const hashIdx = (fileLink || '').indexOf('#');
  if (hashIdx === -1) return userPassphrase;
  const fragment = fileLink.slice(hashIdx + 1);
  const wrappedBytes = Uint8Array.from(atob(fragment), c => c.charCodeAt(0));
  const decrypted = await decryptGPG(wrappedBytes, userPassphrase);
  return new TextDecoder().decode(decrypted).trim();
}

async function decryptFile(file, matchedRow, userPassphrase, onStatus) {
  onStatus('decrypting');
  const raw = new Uint8Array(await file.arrayBuffer());

  // SHA256 integrity check — catches bad downloads early
  const expectedHash = (matchedRow?.['GPG_SHA256'] || '').toLowerCase().trim();
  if (expectedHash) {
    const actualHash = await sha256hex(raw);
    if (actualHash !== expectedHash) {
      throw new Error(`SHA256 mismatch — file is corrupted or is the wrong file.\nExpected: ${expectedHash.slice(0, 16)}…\nGot:      ${actualHash.slice(0, 16)}…`);
    }
  }

  const fileLink = matchedRow?.['File_Link'] || '';
  const fileType = matchedRow?.['File_Type'] || '';
  const category = matchedRow?.['Data_Type'] || 'Other';
  const passphrase = await resolvePassphrase(fileLink, userPassphrase);

  if (fileType === 'GPG_ZIP_PWD' || fileType === 'GPG_ZIP' || fileType === 'GPG_WITH_ZIP' || fileType === '') {
    const decrypted = await decryptGPG(raw, passphrase);
    onStatus('extracting');
    const zip = await JSZip.loadAsync(decrypted);
    const files = [];
    for (const [name, entry] of Object.entries(zip.files)) {
      if (!entry.dir) {
        const data = await entry.async('uint8array');
        files.push({ path: `${category}/${name}`, data });
      }
    }
    return files;
  }

  if (fileType === 'GPG' || fileType === 'GPG_ONLY') {
    const decrypted = await decryptGPG(raw, passphrase);
    const outName = file.name.replace(/\.gpg$/i, '');
    return [{ path: `${category}/${outName}`, data: decrypted }];
  }

  if (fileType === 'ZIP_PWD' || fileType === 'ZIP_WITH_PWD') {
    const zipName = file.name.replace(/\.[^.]+$/, '') + '.zip';
    return [{ path: `${category}/${zipName}`, data: raw }];
  }

  // Unknown — try GPG decrypt, fall back to raw
  try {
    const decrypted = await decryptGPG(raw, passphrase);
    return [{ path: `${category}/${file.name.replace(/\.gpg$/i, '')}`, data: decrypted }];
  } catch {
    return [{ path: `${category}/${file.name}`, data: raw }];
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const C = {
  bg: '#f8fafc', surface: '#ffffff', border: '#e2e8f0',
  blue: '#0369a1', blueBg: '#e0f2fe',
  text: '#0f172a', muted: '#64748b',
  green: '#15803d', greenBg: '#dcfce7',
  red: '#dc2626', redBg: '#fee2e2',
};

const STATUS_LABEL = { pending: 'Waiting', decrypting: 'Decrypting…', extracting: 'Extracting…', done: 'Done', error: 'Error' };
const STATUS_COLOR = { pending: C.muted, decrypting: '#7c3aed', extracting: C.blue, done: C.green, error: C.red };
const STATUS_BG    = { pending: '#f1f5f9', decrypting: '#ede9fe', extracting: C.blueBg, done: C.greenBg, error: C.redBg };

function StatusBadge({ status }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: STATUS_COLOR[status] || C.muted, background: STATUS_BG[status] || '#f1f5f9' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Spinner() {
  return <span style={{ display: 'inline-block', width: 13, height: 13, border: '2px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.7s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />;
}

function DropZone({ label, sub, accept, multiple, onFiles, active }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const handle = useCallback(fs => { if (fs?.length) onFiles(fs); }, [onFiles]);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files); }}
      onClick={() => ref.current?.click()}
      style={{ border: `2px dashed ${over ? C.blue : active ? '#22c55e' : C.border}`, borderRadius: 10, background: over ? C.blueBg : active ? '#f0fdf4' : C.bg, padding: '20px 24px', textAlign: 'center', cursor: 'pointer', transition: 'all .15s' }}
    >
      <input ref={ref} type="file" accept={accept} multiple={multiple} style={{ display: 'none' }} onChange={e => handle(e.target.files)} />
      <div style={{ fontSize: 22, marginBottom: 6 }}>{active ? '✓' : '📁'}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: active ? C.green : C.text }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── Parse link rows from CSV text or XLSX file object ───────────────────────
async function parseLinkSource(file) {
  const name = file.name.toLowerCase();
  let rows = [];
  if (name.endsWith('.csv')) {
    rows = parseCSV(await file.text());
  } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    rows = await parseXLSX(file);
  } else {
    throw new Error('Expected a CSV or Excel file inside the ZIP.');
  }
  if (!rows.length) throw new Error('No rows found.');
  if (!rows[0]['File_Link'] && !rows[0]['File Link']) throw new Error('No File_Link column found.');
  if (!rows[0]['File_Link'] && rows[0]['File Link']) {
    rows = rows.map(r => ({ ...r, 'File_Link': r['File Link'], 'File_Name': r['File Name'] || r['File_Name'], 'Data_Type': r['Data Type'] || r['Data_Type'], 'File_Type': r['File Type'] || r['File_Type'], 'GPG_Size': r['GPG Size'] || r['GPG_Size'], 'GPG_SHA256': r['GPG_SHA256'] || r['GPG SHA256'] || '' }));
  }
  return rows;
}

// ─── Main component ────────────────────────────────────────────────────────────
function AppleLegalDownload() {
  const [phase, setPhase] = useState('upload'); // 'upload' | 'decrypting' | 'done'

  // Shared passphrase (used by Step 0 and pre-filled into Step 2)
  const [passphrase, setPassphrase] = useState('');
  const [showPass, setShowPass]     = useState(false);

  // Step 0 — decrypt the initial GPG file to get the link list
  const [s0File, setS0File]           = useState(null);
  const [s0Phase, setS0Phase]         = useState('idle'); // 'idle'|'decrypting'|'done'|'error'
  const [s0Error, setS0Error]         = useState('');
  const [s0DecryptedBytes, setS0DecryptedBytes] = useState(null); // full decrypted ZIP
  const [s0FileList, setS0FileList]   = useState([]); // names of all files inside

  // Step 1 — download helper state
  const [linkRows, setLinkRows]             = useState([]);
  const [linkSourceName, setLinkSourceName] = useState('');
  const [linkError, setLinkError]           = useState('');
  const [downloadTriggered, setDownloadTriggered] = useState(false);

  // Step 2 — decrypt state
  const [appleZip, setAppleZip]   = useState(null);
  const [gpgFiles, setGpgFiles]   = useState([]);
  const [statuses, setStatuses]   = useState({});
  const [errors, setErrors]       = useState({});
  const [outputFiles, setOutputFiles] = useState([]);
  const [globalError, setGlobalError] = useState('');

  const setStatus = useCallback((name, s) => setStatuses(p => ({ ...p, [name]: s })), []);
  const setErr    = useCallback((name, m) => setErrors(p => ({ ...p, [name]: m })), []);

  // ── Step 0: decrypt the initial GPG to extract the link list ────────────────
  async function handleStep0() {
    if (!s0File || !passphrase.trim()) return;
    setS0Phase('decrypting');
    setS0Error('');
    try {
      const raw = new Uint8Array(await s0File.arrayBuffer());
      const decrypted = await decryptGPG(raw, passphrase);
      // Store the full decrypted ZIP bytes for download
      setS0DecryptedBytes(decrypted);

      // Parse the ZIP to find all files and the CSV/Excel link list
      const zip = await JSZip.loadAsync(decrypted);
      const allFiles = Object.entries(zip.files)
        .filter(([, e]) => !e.dir)
        .map(([n]) => n.split('/').pop())
        .filter(n => !n.startsWith('.'));
      setS0FileList(allFiles);

      // Find the CSV or Excel file for Step 1
      const csvEntry = Object.entries(zip.files).find(([n, e]) =>
        !e.dir && (n.toLowerCase().includes('account-download-details') || n.toLowerCase().endsWith('.csv'))
      );
      const xlsxEntry = !csvEntry && Object.entries(zip.files).find(([n, e]) =>
        !e.dir && (n.toLowerCase().endsWith('.xlsx') || n.toLowerCase().endsWith('.xls'))
      );
      const [entryName, entry] = csvEntry || xlsxEntry || [null, null];
      if (!entry) throw new Error('No CSV or Excel file found inside the decrypted ZIP.');

      const blob = new Blob([await entry.async('uint8array')]);
      const fakeFile = new File([blob], entryName.split('/').pop());
      const rows = await parseLinkSource(fakeFile);
      setLinkRows(rows);
      setLinkSourceName(entryName.split('/').pop());
      setDownloadTriggered(false);
      setS0Phase('done');
    } catch (err) {
      setS0Error(err.message);
      setS0Phase('error');
    }
  }

  function downloadS0Zip() {
    const blob = new Blob([s0DecryptedBytes], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = s0File.name.replace(/\.gpg$/i, '');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Step 1: parse a CSV/Excel/ZIP to extract download links ────────────────
  async function handleLinkFile(files) {
    setLinkError('');
    setLinkRows([]);
    setDownloadTriggered(false);
    const file = files[0];
    try {
      let rows;
      if (file.name.toLowerCase().endsWith('.zip')) {
        const csvText = await extractCSVFromZip(file);
        if (!csvText) throw new Error('No CSV found inside the ZIP.');
        rows = parseCSV(csvText);
        if (!rows.length || (!rows[0]['File_Link'] && !rows[0]['File Link'])) throw new Error('No File_Link column found.');
      } else {
        rows = await parseLinkSource(file);
      }
      setLinkRows(rows);
      setLinkSourceName(file.name);
    } catch (err) {
      setLinkError(err.message);
    }
  }

  // ── Trigger one browser download per row ────────────────────────────────────
  // Opens each URL in a new window/tab — the browser downloads .gpg files automatically
  function triggerAllDownloads() {
    setDownloadTriggered(true);
    linkRows.forEach((row, i) => {
      const url = (row['File_Link'] || '').split('#')[0];
      if (!url) return;
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = url;
        // Can't force filename cross-origin, but browser infers from URL path
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, i * 1500);
    });
  }

  // ── Step 2: parse Apple ZIP for metadata, then decrypt .gpg files ───────────
  async function parseAppleZip(file) {
    const csvText = await extractCSVFromZip(file);
    return csvText ? parseCSV(csvText) : [];
  }

  async function handleProcess() {
    trackToolAction('/applelegaldownload', 'Apple Legal Process Download');
    if (!gpgFiles.length) { setGlobalError('Please upload at least one .gpg file.'); return; }
    if (!passphrase.trim()) { setGlobalError('Please enter the decryption passphrase.'); return; }
    setGlobalError('');
    setPhase('decrypting');

    // Build lookup map from CSV metadata (ZIP or already have linkRows from step 1)
    const rowsByName = {};
    if (linkRows.length) {
      linkRows.forEach(r => { rowsByName[r['File_Name']] = r; });
    } else if (appleZip) {
      try {
        const rows = await parseAppleZip(appleZip);
        rows.forEach(r => { rowsByName[r['File_Name']] = r; });
      } catch { /* proceed without metadata */ }
    }

    const collected = [];
    for (const file of gpgFiles) {
      const matchedRow = rowsByName[file.name];
      setStatus(file.name, 'decrypting');
      try {
        const files = await decryptFile(file, matchedRow, passphrase, s => setStatus(file.name, s));
        files.forEach(f => collected.push(f));
        setStatus(file.name, 'done');
      } catch (err) {
        setStatus(file.name, 'error');
        setErr(file.name, err.message);
      }
    }
    setOutputFiles(collected);
    setPhase('done');
  }

  async function downloadOutputZip() {
    const zip = new JSZip();
    outputFiles.forEach(({ path, data }) => zip.file(path, data));
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Apple_Legal_Process_${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function reset() {
    setPhase('upload');
    setS0File(null); setS0Phase('idle'); setS0Error(''); setS0DecryptedBytes(null); setS0FileList([]);
    setLinkRows([]); setLinkSourceName(''); setLinkError(''); setDownloadTriggered(false);
    setAppleZip(null); setGpgFiles([]); setPassphrase('');
    setStatuses({}); setErrors({}); setOutputFiles([]); setGlobalError('');
  }

  // ── Step 3: flatten & reorganize by account ─────────────────────────────────
  const [s3File, setS3File]           = useState(null);
  const [s3Phase, setS3Phase]         = useState('idle'); // 'idle'|'processing'|'done'|'error'
  const [s3Error, setS3Error]         = useState('');
  const [s3Output, setS3Output]       = useState([]);
  const [s3Accounts, setS3Accounts]   = useState({});

  async function handleOrganize() {
    if (!s3File) return;
    setS3Phase('processing');
    setS3Error('');
    try {
      const zip = await JSZip.loadAsync(s3File);
      const outFiles = [];
      const accounts = {};

      for (const [entryPath, entry] of Object.entries(zip.files)) {
        if (entry.dir) continue;
        // Split and strip leading empty segments + .DS_Store
        let parts = entryPath.split('/').filter(p => p && !p.startsWith('.'));
        if (!parts.length) continue;
        const fileName = parts[parts.length - 1];
        if (fileName.startsWith('.')) continue;

        // Strip a top-level ZIP root folder if present (e.g. "Apple_Legal_Process_2026-03-04")
        if (parts.length > 4 && /^Apple_Legal_Process_\d/.test(parts[0])) {
          parts = parts.slice(1);
        }

        // Expected: [Data_Type, AppleID, CaseNum, AccountFolder, ...SubFolders, FileName]
        // AccountFolder is at index 3 (e.g. "barnesc77@icloud.com-2018190")
        // Fallback for shallower structures
        let dataType = parts[0] || 'Other';
        let accountFolder;
        if (parts.length >= 5) {
          accountFolder = parts[3]; // email-casenum
        } else if (parts.length >= 2) {
          // Try to find a part containing '@'
          accountFolder = parts.find((p, i) => i > 0 && p.includes('@')) || parts[1];
        } else {
          accountFolder = 'Unknown';
        }

        const outPath = `${accountFolder}/${dataType}/${fileName}`;
        const data = await entry.async('uint8array');
        outFiles.push({ path: outPath, data });

        if (!accounts[accountFolder]) accounts[accountFolder] = {};
        accounts[accountFolder][dataType] = (accounts[accountFolder][dataType] || 0) + 1;
      }

      if (!outFiles.length) throw new Error('No files found in the ZIP.');
      setS3Output(outFiles);
      setS3Accounts(accounts);
      setS3Phase('done');
    } catch (err) {
      setS3Error(err.message);
      setS3Phase('error');
    }
  }

  async function downloadS3Zip() {
    const zip = new JSZip();
    s3Output.forEach(({ path, data }) => zip.file(path, data));
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = s3File.name.replace(/\.zip$/i, '_flattened.zip');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const card = { background: C.surface, borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', padding: '24px 26px', marginBottom: 20 };
  const btn = (bg = C.blue, extra = {}) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', background: bg, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', ...extra });

  const doneCount  = Object.values(statuses).filter(s => s === 'done').length;
  const errorCount = Object.values(statuses).filter(s => s === 'error').length;

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Navbar pageTitle="Apple Legal Process Download" />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '36px 24px 80px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: C.text }}>

        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.4px' }}>Apple Legal Process Download</h2>
          <p style={{ color: C.muted, fontSize: 15, margin: 0, lineHeight: 1.6 }}>
            Start with Step 0 to decrypt Apple's initial GPG file and extract the download links automatically.
          </p>
        </div>

        <div role="alert" style={{ display: 'flex', gap: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
          <span style={{ fontSize: 16, lineHeight: 1.4 }}>⚠️</span>
          <div style={{ fontSize: 13.5, color: '#92400e', lineHeight: 1.6 }}>
            <strong>Not for search warrant returns.</strong> Warrant returns (iCloud content, backups, photos) are too large to
            download and decrypt in a browser. Use this for subpoena and 2703(d) returns.
          </div>
        </div>

        {/* ── STEP 0: DECRYPT INITIAL GPG ── */}
        {phase === 'upload' && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, background: '#f3e8ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 20, letterSpacing: '.04em' }}>STEP 0</span>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Decrypt Apple's Initial File</span>
            </div>
            <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>
              Upload the <strong>.gpg file you received from Apple</strong>. Decrypting it will automatically extract the download links and pre-fill Step 1.
            </p>

            <DropZone
              label={s0File ? s0File.name : 'Drop .gpg file here'}
              sub="e.g. original.zip.gpg from Apple"
              accept=".gpg"
              multiple={false}
              onFiles={fs => { setS0File(fs[0]); setS0Phase('idle'); setS0Error(''); }}
              active={s0Phase === 'done'}
            />

            <div style={{ position: 'relative', margin: '12px 0' }}>
              <input
                type={showPass ? 'text' : 'password'}
                placeholder="Decryption passphrase"
                value={passphrase}
                onChange={e => setPassphrase(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleStep0(); }}
                style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 60px 10px 14px', fontSize: 14, color: C.text, background: C.surface, outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 12, color: C.muted, cursor: 'pointer' }}>
                {showPass ? 'hide' : 'show'}
              </button>
            </div>

            {s0Error && (
              <div style={{ fontSize: 13, color: C.red, background: C.redBg, borderRadius: 8, padding: '8px 12px', marginBottom: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{s0Error}</div>
            )}

            {s0Phase === 'done' ? (
              <>
                <div style={{ background: C.greenBg, border: `1px solid #bbf7d0`, borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: s0FileList.length ? 8 : 0 }}>
                    <span style={{ fontSize: 16 }}>✓</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Decrypted successfully</div>
                      <div style={{ fontSize: 12, color: C.green }}>{linkRows.length} download link{linkRows.length !== 1 ? 's' : ''} found — Step 1 is ready below</div>
                    </div>
                  </div>
                  {s0FileList.length > 0 && (
                    <div style={{ borderTop: '1px solid #bbf7d0', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.green, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Files inside:</div>
                      {s0FileList.map(f => (
                        <div key={f} style={{ fontSize: 11, fontFamily: 'monospace', color: '#166534' }}>• {f}</div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={downloadS0Zip} style={{ ...btn('#15803d'), width: '100%' }}>
                  ↓ Download All Decrypted Files
                </button>
              </>
            ) : (
              <button
                onClick={handleStep0}
                disabled={!s0File || !passphrase.trim() || s0Phase === 'decrypting'}
                style={{ ...btn((!s0File || !passphrase.trim() || s0Phase === 'decrypting') ? '#94a3b8' : '#7c3aed'), width: '100%' }}
              >
                {s0Phase === 'decrypting' ? <><Spinner />Decrypting…</> : 'Decrypt & Extract Links →'}
              </button>
            )}
          </div>
        )}

        {/* ── STEP 1: DOWNLOAD LINKS ── */}
        {phase === 'upload' && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, background: C.blueBg, color: C.blue, padding: '2px 8px', borderRadius: 20, letterSpacing: '.04em' }}>STEP 1</span>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Get the Encrypted Files</span>
              <span style={{ fontSize: 12, color: C.muted }}>(optional — skip if you already have the .gpg files)</span>
            </div>
            <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>
              Upload the <strong>CSV or Excel file</strong> from Apple (the one with all the download links).
              The tool will parse the links and trigger your browser to download each .gpg file.
            </p>

            <DropZone
              label={linkSourceName || 'Drop CSV or Excel file here'}
              sub="account-download-details.csv / .xlsx from Apple"
              accept=".csv,.xlsx,.xls,.zip"
              multiple={false}
              onFiles={handleLinkFile}
              active={linkRows.length > 0}
            />

            {linkError && (
              <div style={{ marginTop: 10, fontSize: 13, color: C.red, background: C.redBg, borderRadius: 8, padding: '8px 12px' }}>{linkError}</div>
            )}

            {linkRows.length > 0 && (
              <>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', margin: '14px 0' }}>
                  <div style={{ padding: '7px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${C.border}` }}>
                    {linkRows.length} file{linkRows.length !== 1 ? 's' : ''} found
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {linkRows.map((row, i) => (
                      <div key={i} style={{ padding: '8px 12px', borderBottom: i < linkRows.length - 1 ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{row['Data_Type'] || 'Unknown'}</div>
                          <div style={{ fontSize: 11, fontFamily: 'monospace', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row['File_Name']}</div>
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap' }}>{row['GPG_Size'] || ''}</div>
                        <a
                          href={(row['File_Link'] || '').split('#')[0]}
                          style={{ ...btn('#475569', { fontSize: 11, padding: '4px 10px', textDecoration: 'none', whiteSpace: 'nowrap' }) }}
                        >
                          ↓
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                <button onClick={triggerAllDownloads} style={{ ...btn(downloadTriggered ? '#475569' : C.blue), width: '100%' }}>
                  {downloadTriggered
                    ? `✓ Downloads triggered — check your Downloads folder`
                    : `↓ Download All ${linkRows.length} Files (staggered)`}
                </button>
                {!downloadTriggered && (
                  <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 5 }}>
                    Files will open one every 1.5 seconds to avoid browser popup blocking
                  </div>
                )}
                {downloadTriggered && (
                  <div style={{ fontSize: 12, color: C.green, textAlign: 'center', marginTop: 6 }}>
                    Once all files are in your Downloads folder, upload them in Step 2 below.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── STEP 2: DECRYPT ── */}
        {phase === 'upload' && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, background: C.greenBg, color: C.green, padding: '2px 8px', borderRadius: 20, letterSpacing: '.04em' }}>STEP 2</span>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Decrypt &amp; Organize</span>
            </div>
            <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>
              Upload all the downloaded <strong>.gpg files</strong> and enter the passphrase. The tool will decrypt and organize everything into category folders.
              {linkRows.length > 0
                ? <span style={{ color: C.green }}> Category mapping from Step 1 will be used automatically.</span>
                : <span> For correct folder names, complete Step 1 first.</span>}
            </p>

            {globalError && (
              <div style={{ background: C.redBg, border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.red }}>{globalError}</div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Encrypted .gpg Files <span style={{ color: C.red }}>*</span>
              </div>
              <DropZone
                label={gpgFiles.length ? `${gpgFiles.length} file${gpgFiles.length !== 1 ? 's' : ''} selected` : 'Drop all .gpg files here'}
                sub="Select all downloaded .gpg files at once"
                accept=".gpg"
                multiple={true}
                onFiles={fs => setGpgFiles(Array.from(fs))}
                active={gpgFiles.length > 0}
              />
            </div>

            {gpgFiles.length > 0 && (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
                {gpgFiles.map((f, i) => (
                  <div key={i} style={{ padding: '6px 12px', borderBottom: i < gpgFiles.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: 12, fontFamily: 'monospace', color: C.muted, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{f.name}</span>
                    <span>{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ position: 'relative', marginBottom: 14 }}>
              <input
                type={showPass ? 'text' : 'password'}
                placeholder="Decryption passphrase"
                value={passphrase}
                onChange={e => setPassphrase(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleProcess(); }}
                style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${s0Phase === 'done' ? '#bbf7d0' : C.border}`, borderRadius: 8, padding: '10px 60px 10px 14px', fontSize: 14, color: C.text, background: s0Phase === 'done' ? '#f0fdf4' : C.surface, outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 12, color: C.muted, cursor: 'pointer' }}>
                {showPass ? 'hide' : 'show'}
              </button>
              {s0Phase === 'done' && (
                <div style={{ fontSize: 11, color: C.green, marginTop: 4 }}>✓ Pre-filled from Step 0</div>
              )}
            </div>

            <button onClick={handleProcess} style={{ ...btn(), width: '100%' }}>
              Decrypt &amp; Organize →
            </button>
          </div>
        )}

        {/* ── DECRYPTING ── */}
        {phase === 'decrypting' && (
          <div style={card}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Decrypting Files…</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
              {doneCount} of {gpgFiles.length} complete{errorCount > 0 ? ` · ${errorCount} error${errorCount !== 1 ? 's' : ''}` : ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gpgFiles.map((file, i) => {
                const st = statuses[file.name] || 'pending';
                const active = st !== 'pending' && st !== 'done' && st !== 'error';
                return (
                  <div key={i} style={{ padding: '10px 14px', border: `1px solid ${C.border}`, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                    {active && <Spinner />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontFamily: 'monospace', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                      {errors[file.name] && <div style={{ fontSize: 11, color: C.red, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errors[file.name]}</div>}
                    </div>
                    <StatusBadge status={st} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── DONE ── */}
        {phase === 'done' && (
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>✓</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Complete</div>
                <div style={{ fontSize: 13, color: C.muted }}>
                  {doneCount} file{doneCount !== 1 ? 's' : ''} decrypted · {outputFiles.length} output file{outputFiles.length !== 1 ? 's' : ''}
                  {errorCount > 0 ? ` · ${errorCount} error${errorCount !== 1 ? 's' : ''}` : ''}
                </div>
              </div>
            </div>

            {outputFiles.length > 0 && (() => {
              const byCategory = {};
              outputFiles.forEach(({ path }) => {
                const cat = path.split('/')[0];
                byCategory[cat] = (byCategory[cat] || 0) + 1;
              });
              return (
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
                  <div style={{ padding: '7px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${C.border}` }}>
                    Output folder structure
                  </div>
                  {Object.entries(byCategory).map(([cat, count]) => (
                    <div key={cat} style={{ padding: '9px 14px', display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>📁 {cat}</span>
                      <span style={{ color: C.muted }}>{count} file{count !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {errorCount > 0 && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: C.redBg, borderRadius: 8, fontSize: 12, color: C.red }}>
                <strong>Errors:</strong>
                {Object.entries(errors).map(([fn, msg]) => (
                  <div key={fn} style={{ marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>{fn}:</strong> {msg}
                  </div>
                ))}
              </div>
            )}

            <button onClick={downloadOutputZip} style={{ ...btn(C.green), width: '100%', marginBottom: 10, fontSize: 15 }}>
              ↓ Download Organized ZIP
            </button>
            <button onClick={reset} style={{ ...btn('#475569'), width: '100%' }}>
              ← Start Over
            </button>
          </div>
        )}

        {/* ── STEP 3: FLATTEN & REORGANIZE ── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 20, letterSpacing: '.04em' }}>STEP 3</span>
            <span style={{ fontSize: 15, fontWeight: 700 }}>Flatten &amp; Organize by Account</span>
            <span style={{ fontSize: 12, color: C.muted }}>(optional)</span>
          </div>
          <p style={{ fontSize: 13, color: C.muted, margin: '0 0 14px', lineHeight: 1.6 }}>
            Upload the ZIP from Step 2. The tool will flatten the deep folder structure into{' '}
            <code style={{ fontSize: 12, background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>AccountName / Category / file</code>.
          </p>

          <DropZone
            label={s3File ? s3File.name : 'Drop Step 2 output ZIP here'}
            sub="Apple_Legal_Process_YYYY-MM-DD.zip"
            accept=".zip"
            multiple={false}
            onFiles={fs => { setS3File(fs[0]); setS3Phase('idle'); setS3Output([]); setS3Accounts({}); setS3Error(''); }}
            active={!!s3File && s3Phase !== 'idle'}
          />

          {s3Error && (
            <div style={{ marginTop: 10, fontSize: 13, color: C.red, background: C.redBg, borderRadius: 8, padding: '8px 12px' }}>{s3Error}</div>
          )}

          {s3File && s3Phase !== 'done' && (
            <button
              onClick={handleOrganize}
              disabled={s3Phase === 'processing'}
              style={{ ...btn(s3Phase === 'processing' ? '#94a3b8' : '#7c3aed'), width: '100%', marginTop: 12 }}
            >
              {s3Phase === 'processing' ? <><Spinner />Organizing…</> : 'Flatten & Organize →'}
            </button>
          )}

          {s3Phase === 'done' && (
            <>
              {/* Per-account breakdown */}
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', margin: '14px 0' }}>
                <div style={{ padding: '7px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${C.border}` }}>
                  {Object.keys(s3Accounts).length} account{Object.keys(s3Accounts).length !== 1 ? 's' : ''} · {s3Output.length} file{s3Output.length !== 1 ? 's' : ''}
                </div>
                {Object.entries(s3Accounts).map(([account, cats]) => (
                  <div key={account} style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>📁 {account}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {Object.entries(cats).map(([cat, count]) => (
                        <span key={cat} style={{ fontSize: 11, background: C.blueBg, color: C.blue, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                          {cat} ({count})
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={downloadS3Zip} style={{ ...btn('#7c3aed'), width: '100%', marginBottom: 8, fontSize: 15 }}>
                ↓ Download Reorganized ZIP
              </button>
              <button
                onClick={() => { setS3File(null); setS3Phase('idle'); setS3Output([]); setS3Accounts({}); setS3Error(''); }}
                style={{ ...btn('#475569'), width: '100%' }}
              >
                Reset Step 3
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default AppleLegalDownload;
